"use client";

import { useState } from "react";
import { Button, type ButtonProps } from "./Button";
import {
  Dialog,
  DialogPortal,
  DialogBackdrop,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
} from "./Dialog";

export interface ConfirmButtonProps {
  /** Label for the trigger button. Also forms the confirmation question. */
  text: string;
  /** Invoked once the action is confirmed. */
  onUpdate: () => void;
  /** Visual variant of the trigger button. */
  variant?: ButtonProps["variant"];
  /** Text color of the trigger button — only meaningful for `variant="tertiary"`. */
  color?: ButtonProps["color"];
  /** Title of the confirmation dialog. */
  title?: string;
}

/**
 * ConfirmButton Component
 *
 * A destructive action paired with the confirmation step it requires. Renders
 * a trigger button that opens a Dialog before invoking the action.
 *
 * Laws:
 * - Every destructive action is confirmed — never wire one up bare
 * - `text` is the action, not the object ("Delete role", not "Delete")
 * - Safe to nest inside another Dialog; base-ui tracks nesting, so Escape and
 *   outside-press resolve against the confirmation while it is open
 *
 * Built on the design-system Dialog, not MUI's. MUI's modal sits at z-index
 * 1300 and this one at --z-modal (1310), so an MUI confirmation nested inside
 * a design-system Dialog opens *behind* the parent's backdrop — invisible and
 * unclickable.
 */
export function ConfirmButton({
  text,
  onUpdate,
  variant = "destructive",
  color,
  title = "Confirm action",
}: ConfirmButtonProps) {
  const [open, setOpen] = useState(false);

  const handleConfirm = () => {
    onUpdate();
    setOpen(false);
  };

  return (
    <>
      <Button variant={variant} color={color} onClick={() => setOpen(true)}>
        {text}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogBackdrop />
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogClose aria-label="Close" />
            </DialogHeader>
            <DialogBody>
              <DialogDescription>
                Are you sure you want to {text.toLowerCase()}? This cannot be
                undone.
              </DialogDescription>
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirm}>
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
}
